import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  Users,
  Plus,
  Search,
  Trash2,
  Edit2,
  Loader2,
  UserCheck,
  UserX,
  Key,
  Mail,
  Phone,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import { SERVICE_KINDS, SERVICE_KIND_LABELS, type ServiceKind } from "@ckgo2/shared/types";
import toast from "react-hot-toast";
import ConfirmModal from "@/components/common/ConfirmModal";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roleLabel: string;
  phone?: string;
  avatar?: string;
  isActive: boolean;
  queueKinds?: string[];
  lastLoginAt?: string;
  createdAt: string;
}

interface Role {
  value: string;
  label: string;
  description: string;
}

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  editor: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  marketing: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  sales: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  readonly: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<ApiResponse<Role[]>>("/api/admin/users/roles"),
  });

  const users = usersData?.data || [];
  // Fallback roles in case API hasn't loaded yet
  const roles = rolesData?.data || [
    { value: "admin", label: "Administrator", description: "Full access to all features" },
    {
      value: "editor",
      label: "Content Editor",
      description: "Manage trips, destinations, guides, reviews and media",
    },
    {
      value: "marketing",
      label: "Marketing",
      description: "Newsletter, segments, automated campaigns, customers",
    },
    { value: "sales", label: "Sales", description: "Orders, customers, inquiries" },
    {
      value: "readonly",
      label: "Read Only",
      description: "View only without editing capabilities",
    },
  ];

  // Filter users
  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.roleLabel.toLowerCase().includes(search.toLowerCase()),
  );

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiResponse<{ isActive: boolean }>>(`/api/admin/users/${id}/toggle`),
    onSuccess: () => {
      toast.success("User status changed");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to change user status");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`),
    onSuccess: () => {
      toast.success("User deleted");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete user");
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const handleDelete = (user: User) => {
    setConfirmDelete({ id: user.id, name: user.name });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage administration access</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-5 w-5" />
          New User
        </button>
      </div>

      {/* Role legend */}
      <div className="flex flex-wrap gap-3">
        {roles.map((role) => (
          <div
            key={role.value}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            title={role.description}
          >
            <span className={`badge ${ROLE_COLORS[role.value]}`}>{role.label}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{role.description}</span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Users table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
          </div>
        ) : usersError ? (
          <div className="text-error-500 flex h-32 flex-col items-center justify-center">
            <p className="font-medium">Error loading users</p>
            <p className="text-sm text-gray-500">{(usersError as Error).message}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <Users className="mb-2 h-8 w-8 text-gray-300" />
            <p>No users</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-10 w-10 items-center justify-center rounded-full">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`badge ${ROLE_COLORS[user.role]}`}>{user.roleLabel}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                          user.isActive
                            ? "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400"
                            : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {user.isActive ? (
                          <>
                            <UserCheck className="h-3 w-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <UserX className="h-3 w-3" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never"}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setResetPasswordUser(user)}
                          className="btn-ghost btn-sm"
                          title="Reset password"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleMutation.mutate(user.id)}
                          disabled={toggleMutation.isPending}
                          className="btn-ghost btn-sm"
                          title={user.isActive ? "Deactivate" : "Activate"}
                        >
                          {user.isActive ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setEditingUser(user)}
                          className="btn-ghost btn-sm"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && <UserFormModal roles={roles} onClose={() => setShowCreateModal(false)} />}

      {/* Edit Modal */}
      {editingUser && (
        <UserFormModal user={editingUser} roles={roles} onClose={() => setEditingUser(null)} />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        isLoading={deleteMutation.isPending}
        title="Delete user"
        message={
          confirmDelete
            ? `Are you sure you want to delete user ${confirmDelete.name}? This cannot be undone.`
            : ""
        }
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

// User Form Modal (Create/Edit)
function UserFormModal({
  user,
  roles,
  onClose,
}: {
  user?: User;
  roles: Role[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!user;

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [role, setRole] = useState(user?.role || "readonly");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [queueKinds, setQueueKinds] = useState<string[]>(user?.queueKinds || []);
  const toggleKind = (k: string) =>
    setQueueKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      email: string;
      password: string;
      role: string;
      phone?: string;
      isActive: boolean;
      queueKinds: string[];
    }) => api.post<ApiResponse<User>>("/api/admin/users", data),
    onSuccess: () => {
      toast.success("User created");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create user");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      email?: string;
      role?: string;
      phone?: string;
      password?: string;
      isActive?: boolean;
      queueKinds?: string[];
    }) => api.put<ApiResponse<User>>(`/api/admin/users/${user?.id}`, data),
    onSuccess: () => {
      toast.success("User updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update user");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      toast.error("Please fill in name and email");
      return;
    }

    if (!isEdit && (!password || password.length < 8)) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (isEdit) {
      updateMutation.mutate({
        name,
        email,
        role,
        phone: phone || undefined,
        password: password || undefined,
        isActive,
        queueKinds,
      });
    } else {
      createMutation.mutate({
        name,
        email,
        password,
        role,
        phone: phone || undefined,
        isActive,
        queueKinds,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {isEdit ? "Edit User" : "New User"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@company.com"
                className="input pl-10"
                required
              />
            </div>
          </div>

          <div>
            <label className="label">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+84 966 680 323"
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="label">Role *</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
              required
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} - {r.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Specialist queue</label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_KINDS.map((k) => {
                const on = queueKinds.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleKind(k)}
                    className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                      on
                        ? "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    }`}
                  >
                    {SERVICE_KIND_LABELS[k as ServiceKind]}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              The service-booking kinds this person works (e.g. Flight for a flight specialist). The
              service queue lands them pre-filtered to these. Garants are routed by destination
              instead (set on the destination).
            </p>
          </div>

          <div>
            <label className="label">
              Password {isEdit ? "(leave empty to keep current)" : "*"}
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? "••••••••" : "Min. 8 characters"}
                className="input pl-10"
                required={!isEdit}
                minLength={isEdit ? 0 : 8}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="text-primary-600 h-4 w-4 rounded"
            />
            <label htmlFor="isActive" className="text-gray-700 dark:text-gray-300">
              Account is active
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={isPending} className="btn-primary">
              {isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isEdit ? (
                "Save"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reset Password Modal
function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");

  const resetMutation = useMutation({
    mutationFn: (newPassword: string) =>
      api.post(`/api/admin/users/${user.id}/reset-password`, { password: newPassword }),
    onSuccess: () => {
      toast.success("Password has been reset");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reset password");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    resetMutation.mutate(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Reset Password</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Set a new password for user <strong>{user.name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label">New Password *</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="input pl-10"
                required
                minLength={8}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={resetMutation.isPending} className="btn-primary">
              {resetMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
